/**
 * URL-param parsing — extracts gameKey + lobby session params.
 *
 *   ?gamekey=BAL12        → single-player Live Ball 12
 *   ?gamekey=BAL12M       → multiplayer Live Ball 12
 *   ?token=…&gsid=…       → real lobby launch (skips dev login)
 *   ?user=&agentId=       → override defaults for dev login path
 *
 * Returns `multiplayer: boolean` derived from the gameKey suffix and exposes
 * BOTH the variant key (for UI / payouts) and the API game key (for the
 * /action/opengame and SignalR JoinGame calls — the M-suffixed form when in MP).
 */
import { DEFAULT_AGENT_ID, DEFAULT_USER_NAME } from '../config';
import type { GameKey } from '../types/game';
import { apiGameKey, resolveGameKey } from './variants';

export interface UrlParams {
  /** Variant identity (used for layouts, payouts, ball count). */
  gameKey: GameKey;
  /** Server-facing key — equals gameKey for single, gameKey+'M' for multi. */
  apiGameKey: string;
  multiplayer: boolean;
  token: string | null;
  gsid: string | null;
  userName: string;
  agentId: string;
  isSound: boolean;
  exitUrl: string | null;
}

const DEFAULT_GAME_KEY: GameKey = 'BAL6';

export function loadUrlParams(): UrlParams {
  const q = new URLSearchParams(window.location.search);
  // Lobby's openGame response uses `?g=BAL6` (lowercase one-letter), so accept
  // that first; fall back to the more descriptive aliases for direct dev URLs.
  const rawKey =
    q.get('g') ?? q.get('gamekey') ?? q.get('gameKey') ?? q.get('game') ?? q.get('gk');
  const resolved = resolveGameKey(rawKey);
  const variant = resolved?.variant ?? DEFAULT_GAME_KEY;
  const multiplayer = resolved?.multiplayer ?? false;
  return {
    gameKey: variant,
    apiGameKey: apiGameKey(variant, multiplayer),
    multiplayer,
    token: q.get('token'),
    gsid: q.get('gsid'),
    userName: q.get('user') ?? q.get('userName') ?? DEFAULT_USER_NAME,
    agentId: q.get('agentId') ?? DEFAULT_AGENT_ID,
    isSound: q.get('isSound') === 'true' || q.get('isSound') === '1',
    exitUrl: q.get('exitUrl'),
  };
}
